/* eslint-disable @typescript-eslint/no-shadow */
import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { makeStyles } from "@material-ui/core/styles";
import {
  KButton,
  KDialog,
  KIcon,
} from "@kyburz/kdesign-system";

import {
  getVehicleModels,
  deleteVehicleModel,
} from "../../apiCalls/admin/vehicleModels";
import { setAlert } from "../../redux/actions/actions";
import { formatDate } from "../../utils/timeUtils";
import useSetState from "../../hooks/useSetState";

import NewVehicleModel from "./NewVehicleModel";
import { RootStateInterface } from "../../interfaces/ReduxInterface";
import KInfiniteTable from "../../components/table/KInfiniteTable";

const MemoizedKInfiniteTable = React.memo(
  (props: any) => (
    <KInfiniteTable
      data={props.data}
      hasNextPage={props.hasNextPage}
      isNextPageLoading={props.isNextPageLoading}
      loadNextPage={props.loadNextPage}
      columns={props.columns}
      sortTable={props.sortTable}
      initialSortBy={props.initialSortBy}
      manualSortBy={true}
      hasStickyColumns={true}
      hasFilter
      manualFilters
      filterTable={props.filterTable}
      loading={props.loading}
    />
  ),
  (oldProps, newProps) => {
    return (
      oldProps.data === newProps.data && oldProps.loading === newProps.loading
    );
  },
);

const useStyles = makeStyles(() => ({
  ctaContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  iconContainer: {
    display: "inline-block",
    cursor: "pointer",
  },
  tableContainer: {
    height: "calc(100% - 100px)",
  },
}));

const VehicleModels = () => {
  const currentCustomer = useSelector(
    (state: RootStateInterface) => state.customer.currentCustomer,
  );
  const classes = useStyles();
  const firstUpdate = React.useRef(true);
  const dispatch = useDispatch();
  const { CancelToken } = axios;

  const defaultSortingRules = {
    id: "brand",
    order: "asc",
  };

  const orderByMap = {
    type: "vehicle_type.name",
  };

  const getOrderByValue = (value) => {
    return orderByMap[value] || value;
  };

  const [
    {
      query,
      pagesLoaded,
      hasNextPage,
      isNextPageLoading,
      items,
      deleteModalOpen,
      modelToDelete,
      reload,
      newModelOpen,
      vehicleModelData,
      cancelTokenSource,
      orderBy,
      order,
      filterBy,
      loading,
    },
    setState,
  ] = useSetState({
    query: "",
    pagesLoaded: 0,
    hasNextPage: true,
    isNextPageLoading: false,
    items: [],
    deleteModalOpen: false,
    modelToDelete: "",
    reload: false,
    newModelOpen: false,
    vehicleModelData: {},
    cancelTokenSource: null,
    orderBy: defaultSortingRules.id,
    order: defaultSortingRules.order,
    filterBy: "",
    loading: true,
  });

  const sortTable = (data) => {
    const rules = data[0];
    if (rules) {
      setState({
        orderBy: rules.id,
        order: rules.desc ? "desc" : "asc",
      });
    }
  };

  const toggleDeletePopup = (e, row) =>
    setState({ deleteModalOpen: !deleteModalOpen, modelToDelete: row });

  const toggleNewModelPopUp = (withReload = false) => {
    if (newModelOpen) {
      setState({ vehicleModelData: {} });
    }
    setState({ newModelOpen: !newModelOpen, reload: withReload });
  };

  const handleEdit = (row): void => {
    setState({ vehicleModelData: row.row.original });
    toggleNewModelPopUp();
  };

  const handleDelete = async () => {
    const res = await deleteVehicleModel(
      currentCustomer?.shortname,
      modelToDelete?.id,
    );
    setState({ deleteModalOpen: false, modelToDelete: "", query: "" });
    if (res.data) {
      dispatch(
        setAlert(`Vehicle Model ${modelToDelete?.model} deleted`, "success"),
      );
      setState({ reload: true });
    } else {
      dispatch(
        setAlert(res.detail || "Unable to delete vehicle model", "error"),
      );
    }
  };

  const loadModelsPage = (query, page, startEmpty = true, filterBy = "") => {
    if (cancelTokenSource !== null) {
      cancelTokenSource.cancel("Search cancelled");
    }
    const source = CancelToken.source();

    setState({
      isNextPageLoading: true,
      cancelTokenSource: source,
      loading: true,
    });
    getVehicleModels(
      currentCustomer?.shortname,
      query,
      page,
      60,
      source.token,
      getOrderByValue(orderBy),
      order,
      filterBy,
    )
      .then((res) => {
        const previousArray = startEmpty ? [] : items;

        setState({
          items: [...previousArray].concat(
            ...res.data?.data.map((x) => ({
              id: x._id,
              brand: x.brand,
              model: x.model,
              version: x.version,
              images: x.images,
              created: x.created,
              modified: x.modified,
              type: x.vehicle_type?.name,
            })),
          ),
          isNextPageLoading: false,
          pagesLoaded: page,
          hasNextPage: true,
          cancelToken: null,
          loading: false,
        });
      })
      .catch((e) => {
        setState({
          isNextPageLoading: false,
          pagesLoaded: page,
          cancelToken: null,
        });
        console.error(e);
      });
  };

  const loadNextPage = () =>
    loadModelsPage(query, pagesLoaded + 1, false, filterBy);

  useEffect(() => {
    if (reload) {
      setState({ reload: false });
      loadModelsPage(query, 1, true, filterBy);
    }
  }, [reload]);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    loadModelsPage(query, 1, true, filterBy);
  }, [query]);

  useEffect(() => {
    loadModelsPage(query, 1, true, filterBy);
  }, [order, orderBy, filterBy]);

  const filterTable = (values: { id: string; value: string }[]): void => {
    const parsedFilter: string = values
      .map((x) => `${getOrderByValue(x.id)}:regex:${x.value}`)
      .join(";");
    setState({ filterBy: parsedFilter });
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Brand",
        accessor: "brand",
        defaultCanSort: true,
      },
      {
        Header: "Model",
        accessor: "model",
        defaultCanSort: true,
      },
      {
        Header: "Version",
        accessor: "version",
        defaultCanSort: true,
      },
      {
        Header: "Type",
        accessor: "type",
        defaultCanSort: true,
      },
      {
        id: "created",
        Header: "Created",
        accessor: (row) => formatDate(row.created),
        defaultCanSort: true,
        disableFilters: true,
      },
      {
        Header: "ID",
        accessor: "id",
        disableFilters: true,
      },
      {
        accessor: "actions",
        disableFilters: true,
        Cell: (row) => (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginRight: 16,
            }}
          >
            <div
              className={classes.iconContainer}
              onClick={() => handleEdit(row)}
              style={{ marginRight: "16px" }}
            >
              <KIcon icon="edit" color="#383F51" size={20} />
            </div>
            <div
              className={classes.iconContainer}
              style={{ marginRight: "16px" }}
              onClick={() =>
                setState({
                  deleteModalOpen: true,
                  modelToDelete: row.row.original,
                })
              }
            >
              <KIcon icon="trash-2" color="#383F51" size={20} />
            </div>
          </div>
        ),
        width: 100,
        sticky: "right",
      },
    ],
    [],
  );

  const deletePopup = (
    <KDialog
      size="xs"
      title="Are you sure?"
      open={deleteModalOpen}
      primaryCTAonClick={handleDelete}
      primaryCTAColor="danger"
      primaryCTAText="Delete"
      secondaryCTAonClick={(e) => toggleDeletePopup(e, "")}
      secondaryCTAText="Cancel"
    >
      <p>
        Are you sure you want to delete the vehicle model{" "}
        <b>{modelToDelete?.model}</b>?
      </p>
    </KDialog>
  );

  const newModelPopUp = (
    <NewVehicleModel
      open={newModelOpen}
      handleClose={toggleNewModelPopUp}
      data={vehicleModelData}
    />
  );

  return (
    <>
      <div className={classes.ctaContainer}>
        <div style={{ flexGrow: 1 }} />
        <KButton
          children="Add Model"
          icon={<KIcon icon="plus" />}
          onClick={() => toggleNewModelPopUp()}
        />
      </div>
      <div className={classes.tableContainer}>
        <MemoizedKInfiniteTable
          isNextPageLoading={isNextPageLoading}
          hasNextPage={hasNextPage}
          columns={columns}
          data={items}
          loadNextPage={loadNextPage}
          sortTable={sortTable}
          initialSortBy={[defaultSortingRules]}
          filterTable={filterTable}
          loading={loading}
        />
      </div>
      {newModelPopUp}
      {deletePopup}
    </>
  );
};

export default VehicleModels;
